import { useState } from 'react';
import { ShieldPlus, CheckCircle, XCircle, Activity } from 'lucide-react';
import { useTestingApi } from '../hooks/useTestingApi';
import { INJURY_TYPE, LATERALITY } from '../../enums/testing';
import { IRTPPhaseStatus } from '../../models/rtpProtocol';
import GenericModalV2 from '../../shared/GenericModal/GenericModal';
import CustomTable from '../../shared/CustomTable/CustomTable';
import styles from './RTPDashboard.module.scss';

interface RTPDashboardProps {
  athleteId: string;
}

const INJURY_TYPE_LABELS: Record<INJURY_TYPE, string> = {
  [INJURY_TYPE.LCA]: 'LCA',
  [INJURY_TYPE.MENISCO]: 'Menisco',
  [INJURY_TYPE.CAVIGLIA]: 'Caviglia',
  [INJURY_TYPE.HAMSTRING]: 'Hamstring',
  [INJURY_TYPE.QUAD]: 'Quadricipite',
  [INJURY_TYPE.ALTRO]: 'Altro',
};

const LATERALITY_LABELS: Record<LATERALITY, string> = {
  [LATERALITY.DX]: 'Destra',
  [LATERALITY.SX]: 'Sinistra',
  [LATERALITY.BILATERAL]: 'Bilaterale',
};

const PHASE_LABELS: Record<number, string> = {
  1: 'Fase 1 — Protezione',
  2: 'Fase 2 — Carico progressivo',
  3: 'Fase 3 — Sport specifico',
  4: 'Fase 4 — Ritorno alla gara',
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
}

interface ActivateRTPFormData {
  injuryType: INJURY_TYPE | '';
  injurySide: LATERALITY | '';
  injuryDate: string;
  notes: string;
}

export default function RTPDashboard({ athleteId }: RTPDashboardProps) {
  const { useAthleteRTP, useAthleteRTPCriteria, createRTP, completeRTP } = useTestingApi();

  const rtpQuery = useAthleteRTP(athleteId);
  const criteriaQuery = useAthleteRTPCriteria(athleteId);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const [form, setForm] = useState<ActivateRTPFormData>({
    injuryType: '',
    injurySide: '',
    injuryDate: '',
    notes: '',
  });

  const protocol = rtpQuery.data ?? null;
  const phases: IRTPPhaseStatus[] = criteriaQuery.data ?? [];

  const isLoading = rtpQuery.isLoading;

  const allCriteriaMet =
    phases.length > 0 && phases.every((p) => p.totalPassed === p.totalCriteria);

  const handleOpenModal = () => {
    setForm({ injuryType: '', injurySide: '', injuryDate: '', notes: '' });
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.injuryType || !form.injurySide || !form.injuryDate) return;
    setIsSubmitting(true);
    try {
      await createRTP({
        athleteId,
        injuryDate: form.injuryDate,
        injuryType: form.injuryType,
        injurySide: form.injurySide,
        notes: form.notes || undefined,
      });
      setIsModalOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleComplete = async () => {
    if (!protocol) return;
    setIsCompleting(true);
    try {
      await completeRTP({ protocolId: protocol._id, athleteId });
    } finally {
      setIsCompleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <p className={styles.loadingText}>Caricamento protocollo RTP...</p>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {!protocol || !protocol.isActive ? (
        /* ─── Empty state ─── */
        <>
          <div className={styles.emptyState}>
            <ShieldPlus className={styles.emptyIcon} />
            <p className={styles.emptyTitle}>Nessun protocollo RTP attivo</p>
            <p className={styles.emptySubtitle}>
              Attiva un protocollo Return to Play per monitorare il recupero dell'atleta fase per fase.
            </p>
            <button className={styles.btnPrimary} onClick={handleOpenModal}>
              Attiva protocollo RTP
            </button>
          </div>

          {/* Activation modal */}
          <GenericModalV2
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title="Attiva protocollo RTP"
            size="md"
            isLoading={isSubmitting}
            Footer={
              <div className={styles.formActions}>
                <button
                  className={styles.btnSecondary}
                  onClick={() => setIsModalOpen(false)}
                  disabled={isSubmitting}
                >
                  Annulla
                </button>
                <button
                  className={styles.btnPrimary}
                  onClick={handleSubmit}
                  disabled={
                    isSubmitting ||
                    !form.injuryType ||
                    !form.injurySide ||
                    !form.injuryDate
                  }
                >
                  {isSubmitting ? 'Salvataggio...' : 'Attiva protocollo'}
                </button>
              </div>
            }
          >
            <div className={styles.form}>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Tipo di infortunio</label>
                <select
                  className={styles.formSelect}
                  value={form.injuryType}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, injuryType: e.target.value as INJURY_TYPE }))
                  }
                >
                  <option value="">Seleziona...</option>
                  {(Object.values(INJURY_TYPE) as INJURY_TYPE[]).map((v) => (
                    <option key={v} value={v}>
                      {INJURY_TYPE_LABELS[v]}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formField}>
                <label className={styles.formLabel}>Lato interessato</label>
                <select
                  className={styles.formSelect}
                  value={form.injurySide}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, injurySide: e.target.value as LATERALITY }))
                  }
                >
                  <option value="">Seleziona...</option>
                  {(Object.values(LATERALITY) as LATERALITY[]).map((v) => (
                    <option key={v} value={v}>
                      {LATERALITY_LABELS[v]}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formField}>
                <label className={styles.formLabel}>Data infortunio</label>
                <input
                  type="date"
                  className={styles.formInput}
                  value={form.injuryDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, injuryDate: e.target.value }))
                  }
                />
              </div>

              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  Note
                  <span className={styles.formLabelOptional}>(opzionale)</span>
                </label>
                <textarea
                  className={styles.formTextarea}
                  placeholder="Aggiungi eventuali note cliniche..."
                  value={form.notes}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, notes: e.target.value }))
                  }
                />
              </div>
            </div>
          </GenericModalV2>
        </>
      ) : (
        /* ─── Active protocol view ─── */
        <>
          {/* Banner */}
          <div className={styles.banner}>
            <div className={styles.bannerLeft}>
              <p className={styles.bannerTitle}>
                Protocollo RTP attivo
              </p>
              <div className={styles.bannerMeta}>
                <span className={styles.bannerBadge}>
                  {INJURY_TYPE_LABELS[protocol.injuryType]}
                </span>
                <span className={styles.bannerBadge}>
                  {LATERALITY_LABELS[protocol.injurySide]}
                </span>
                <span className={styles.bannerDate}>
                  Attivo dal {formatDate(protocol.activatedAt)}
                </span>
              </div>
            </div>
            <Activity style={{ width: 32, height: 32, opacity: 0.5 }} />
          </div>

          {/* Phase sections */}
          {criteriaQuery.isLoading ? (
            <div className={styles.loading}>
              <p className={styles.loadingText}>Caricamento criteri...</p>
            </div>
          ) : phases.length === 0 ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyTitle}>Nessun criterio disponibile</p>
              <p className={styles.emptySubtitle}>
                Configura i test abilitati per questo protocollo per visualizzare i criteri di avanzamento.
              </p>
            </div>
          ) : (
            <div className={styles.phases}>
              {phases.map((phaseStatus) => (
                <PhaseCard key={phaseStatus.phase} phaseStatus={phaseStatus} />
              ))}
            </div>
          )}

          {/* Complete button */}
          {phases.length > 0 && (
            <div className={styles.actionsBar}>
              <button
                className={styles.btnComplete}
                disabled={!allCriteriaMet || isCompleting}
                onClick={handleComplete}
                title={
                  !allCriteriaMet
                    ? 'Tutti i criteri devono essere soddisfatti per completare il protocollo'
                    : undefined
                }
              >
                <CheckCircle style={{ width: 16, height: 16 }} />
                {isCompleting ? 'Completamento...' : 'Completa protocollo'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Phase card sub-component ─── */
interface PhaseCardProps {
  phaseStatus: IRTPPhaseStatus;
}

function PhaseCard({ phaseStatus }: PhaseCardProps) {
  const { phase, criteria, totalPassed, totalCriteria, completionPct } = phaseStatus;
  const label = PHASE_LABELS[phase] ?? `Fase ${phase}`;

  return (
    <div className={styles.phaseCard}>
      <div className={styles.phaseHeader}>
        <div className={styles.phaseHeaderLeft}>
          <p className={styles.phaseLabel}>{label}</p>
          <p className={styles.phasePct}>
            {totalPassed}/{totalCriteria} criteri
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className={styles.progressBarOuter}>
            <div
              className={styles.progressBarInner}
              style={{
                width: `${completionPct}%`,
                background: completionPct === 100 ? '#27ae60' : '#0D789D',
              }}
            />
          </div>
          <span
            className={styles.phasePct}
            style={{ minWidth: 36, textAlign: 'right' }}
          >
            {Math.round(completionPct)}%
          </span>
        </div>
      </div>

      {criteria.length === 0 ? (
        <div style={{ padding: '16px 20px' }}>
          <p className={styles.criteriaValueEmpty}>Nessun criterio per questa fase.</p>
        </div>
      ) : (
        <CustomTable
          headers={['Test', 'Valore attuale', 'Soglia', 'Stato']}
          data={criteria}
          fields={{
            Test: {
              key: 'testId',
              customComponent: (_: any, criterion) => (
                <p className={styles.criteriaTestName}>{criterion.testName}</p>
              ),
            },
            'Valore attuale': {
              key: 'currentValue',
              align: 'center',
              customComponent: (_: any, criterion) =>
                criterion.currentValue !== null ? (
                  <p className={styles.criteriaValue}>{criterion.currentValue.toFixed(1)}</p>
                ) : (
                  <p className={styles.criteriaValueEmpty}>—</p>
                ),
            },
            Soglia: {
              key: 'threshold',
              align: 'center',
              customComponent: (_: any, criterion) => (
                <p className={styles.criteriaThreshold}>
                  {criterion.threshold}
                  {criterion.thresholdType === 'lsi' || criterion.thresholdType === 'pre_injury_pct'
                    ? '%'
                    : ''}
                </p>
              ),
            },
            Stato: {
              key: 'passed',
              align: 'center',
              customComponent: (_: any, criterion) => (
                <span
                  className={`${styles.statusBadge} ${
                    criterion.passed ? styles.statusPassed : styles.statusFailed
                  }`}
                  title={criterion.passed ? 'Criterio soddisfatto' : 'Criterio non soddisfatto'}
                >
                  {criterion.passed ? (
                    <CheckCircle style={{ width: 14, height: 14 }} />
                  ) : (
                    <XCircle style={{ width: 14, height: 14 }} />
                  )}
                </span>
              ),
            },
          }}
        />
      )}
    </div>
  );
}
